/**
 * Inside Sales Training - Main Dashboard (Redesigned)
 * Shows pre-training onboarding for first-time users
 * Then displays 20 sessions organized by phase with progress tracking
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import PreTrainingOnboarding from './PreTrainingOnboarding';
import { 
    GraduationCap, Lock, CheckCircle2, Play, Clock, 
    Trophy, Target, BookOpen, Mic, ChevronRight, Award, Briefcase, BarChart3,
    Volume2, Headphones, ArrowRight
} from 'lucide-react';
import { ResumeBanner } from '../../components/gamification';
import MiniCommunicationScoreCard from '../../components/communication/MiniCommunicationScoreCard';

const InsideSalesDashboard = () => {
    const navigate = useNavigate();
    const { api, user, isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [activePhase, setActivePhase] = useState('week_1');
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingComplete, setOnboardingComplete] = useState(false);
    const [incompleteSession, setIncompleteSession] = useState(null);

    useEffect(() => {
        checkOnboardingAndFetchDashboard();
    }, []);

    const checkOnboardingAndFetchDashboard = async () => {
        try {
            // Check if user has completed onboarding
            const onboardingKey = `inside_sales_onboarding_${user?.id}`;
            const hasCompletedOnboarding = localStorage.getItem(onboardingKey);
            
            const [response, incompleteRes] = await Promise.all([
                api.get('/inside-sales/dashboard'),
                api.get('/inside-sales/incomplete-sessions').catch(() => ({ data: { has_incomplete: false } }))
            ]);
            
            setDashboardData(response.data);
            
            // Set incomplete session for resume banner
            if (incompleteRes.data?.has_incomplete && incompleteRes.data?.most_recent) {
                setIncompleteSession(incompleteRes.data.most_recent);
            }
            
            // Show onboarding for first-time users OR users with no progress
            const hasProgress = response.data.progress?.completed_sessions > 0;
            
            if (!hasCompletedOnboarding && !hasProgress) {
                setShowOnboarding(true);
            } else {
                setOnboardingComplete(true);
            }
            
            // Set active phase to current phase if available
            if (response.data.progress?.current_phase) {
                setActivePhase(response.data.progress.current_phase);
            }
        } catch (error) {
            console.error('Failed to load dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOnboardingComplete = (userData) => {
        // Save onboarding completion
        const onboardingKey = `inside_sales_onboarding_${user?.id}`;
        localStorage.setItem(onboardingKey, JSON.stringify({
            completedAt: new Date().toISOString(),
            ...userData
        }));
        
        setShowOnboarding(false);
        setOnboardingComplete(true);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
            case 'in_progress':
                return <Play className="w-5 h-5 text-blue-400" />;
            case 'available':
                return <Target className="w-5 h-5 text-amber-400" />;
            default:
                return <Lock className="w-5 h-5 text-white/30" />;
        }
    };

    const getStatusBadge = (status) => {
        const variants = {
            completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
            in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
            available: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
            failed: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
            locked: 'bg-white/5 text-white/40 border-white/10'
        };
        const labels = {
            completed: 'Completed',
            in_progress: 'In Progress',
            available: 'Start',
            failed: 'Retry',
            locked: 'Locked'
        };
        return (
            <Badge variant="outline" className={variants[status] || variants.locked}>
                {labels[status] || 'Locked'}
            </Badge>
        );
    };

    const getPhaseColor = (phase) => {
        const colors = {
            week_1: 'from-blue-500 to-cyan-500',
            week_2: 'from-purple-500 to-pink-500',
            week_3: 'from-amber-500 to-orange-500',
            week_4: 'from-emerald-500 to-teal-500'
        };
        return colors[phase] || colors.week_1;
    };

    // Show onboarding for first-time users
    if (showOnboarding) {
        return (
            <PreTrainingOnboarding 
                onComplete={handleOnboardingComplete}
                userName={user?.name?.split(' ')[0]}
            />
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(240,10%,4%)] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white text-center py-12">
                <Briefcase className="w-16 h-16 mx-auto text-white/30 mb-4" />
                <h2 className="text-xl font-semibold mb-2">Access Required</h2>
                <p className="text-white/60">Contact your administrator to get access to Inside Sales Training.</p>
            </div>
        );
    }

    const { sessions, phases, progress, certification } = dashboardData;

    return (
        <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white py-8 px-6" data-testid="inside-sales-dashboard">
            <div className="max-w-6xl mx-auto space-y-8">
            {/* Hero Header */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border border-white/10 p-8">
                <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/247676/pexels-photo-247676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')] opacity-10 bg-cover bg-center" />
                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div>
                            <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-4">
                                Voice-Based Training
                            </Badge>
                            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
                                Inside Sales Training
                            </h1>
                            <p className="text-white/70 mt-2 max-w-xl text-lg">
                                Master the art of B2B sales with our comprehensive 20-session program. 
                                Listen, learn, and practice through interactive voice simulations.
                            </p>
                        </div>
                        
                        <div className="flex flex-col gap-3">
                            {isAdmin && (
                                <Button 
                                    variant="outline" 
                                    onClick={() => navigate('/inside-sales/analytics')}
                                    className="border-white/20 text-white hover:bg-white/10"
                                    data-testid="analytics-btn"
                                >
                                    <BarChart3 className="w-4 h-4 mr-2" />
                                    View Analytics
                                </Button>
                            )}
                            
                            {certification?.is_certified && (
                                <Button
                                    onClick={() => navigate('/inside-sales/certificate')}
                                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                                    data-testid="inside-sales-view-certificate-btn"
                                >
                                    <Award className="w-4 h-4 mr-2" />
                                    View Certificate
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Resume Banner - Shows if user has an incomplete session */}
            {incompleteSession && (
                <ResumeBanner
                    sessionName={incompleteSession.title}
                    progress={incompleteSession.progress_percent}
                    lastActive={incompleteSession.last_activity ? new Date(incompleteSession.last_activity).toLocaleDateString() : 'recently'}
                    onResume={() => navigate(`/inside-sales/session/${incompleteSession.session_id}`)}
                    onStartNew={() => setIncompleteSession(null)}
                />
            )}

            {/* Progress Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 border-indigo-500/20">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <GraduationCap className="w-8 h-8 text-indigo-400" />
                            <span className="text-xs text-white/50 uppercase tracking-wider">Progress</span>
                        </div>
                        <p className="text-3xl font-bold text-white">{progress.completed_sessions}/20</p>
                        <Progress 
                            value={(progress.completed_sessions / 20) * 100} 
                            className="mt-3 h-2 bg-white/10" 
                        />
                        <p className="text-xs text-white/50 mt-2">
                            {Math.round((progress.completed_sessions / 20) * 100)}% complete
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Trophy className="w-8 h-8 text-amber-400" />
                            <span className="text-xs text-white/50 uppercase tracking-wider">Avg Score</span>
                        </div>
                        <p className="text-3xl font-bold text-white">
                            {progress.overall_score ? `${Math.round(progress.overall_score * 100)}%` : '--'}
                        </p>
                        <p className="text-xs text-white/50 mt-2">
                            {progress.overall_score >= 0.8 ? 'Excellent performance!' : 'Keep practicing'}
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Clock className="w-8 h-8 text-blue-400" />
                            <span className="text-xs text-white/50 uppercase tracking-wider">Time</span>
                        </div>
                        <p className="text-3xl font-bold text-white">
                            {progress.time_spent_minutes || 0}m
                        </p>
                        <p className="text-xs text-white/50 mt-2">Total time invested</p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <BookOpen className="w-8 h-8 text-emerald-400" />
                            <span className="text-xs text-white/50 uppercase tracking-wider">Current</span>
                        </div>
                        <p className="text-3xl font-bold text-white">
                            {progress.current_session || '✓'}
                        </p>
                        <p className="text-xs text-white/50 mt-2">
                            {progress.current_session ? `Session ${progress.current_session}` : 'All complete!'}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Communication Score Card */}
            <MiniCommunicationScoreCard 
                moduleName="inside_sales" 
                className="bg-gradient-to-r from-indigo-500/5 to-purple-500/5 border-indigo-500/20 text-white [&_*]:text-white/80 [&_.text-muted-foreground]:text-white/50 [&_.text-primary]:text-indigo-400"
            />

            {/* Quick Start Card for users with no progress */}
            {progress.completed_sessions === 0 && (
                <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
                    <CardContent className="p-8">
                        <div className="flex flex-col md:flex-row items-center gap-8">
                            <div className="flex-shrink-0 w-24 h-24 rounded-2xl bg-indigo-500/20 flex items-center justify-center">
                                <Play className="w-10 h-10 text-indigo-400" />
                            </div>
                            <div className="flex-1 text-center md:text-left">
                                <h3 className="text-xl font-bold text-white mb-2">Ready to Start Your Journey?</h3>
                                <p className="text-white/60 mb-4">
                                    Begin with Session 1 to learn about the staffing industry and TekWissen&apos;s unique value proposition.
                                </p>
                                <div className="flex items-center gap-4 justify-center md:justify-start text-sm text-white/50">
                                    <span className="flex items-center gap-2">
                                        <Headphones className="w-4 h-4" />
                                        Use headphones
                                    </span>
                                    <span className="flex items-center gap-2">
                                        <Mic className="w-4 h-4" />
                                        Quiet environment
                                    </span>
                                </div>
                            </div>
                            <Button 
                                onClick={() => navigate('/inside-sales/session/1')}
                                size="lg"
                                className="bg-indigo-600 hover:bg-indigo-700 px-8"
                            >
                                Start Session 1
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Phase Tabs */}
            <Tabs value={activePhase} onValueChange={setActivePhase}>
                <TabsList className="grid grid-cols-4 mb-6 bg-white/5 border border-white/10 p-1 rounded-xl">
                    {phases.map((phase) => (
                        <TabsTrigger 
                            key={phase.phase} 
                            value={phase.phase}
                            className="flex items-center gap-2 data-[state=active]:bg-indigo-500 data-[state=active]:text-white rounded-lg"
                        >
                            <span className="hidden md:inline">{phase.name.split(' ')[0]}</span>
                            <Badge variant="secondary" className="bg-white/10 text-white/70 data-[state=active]:bg-white/20">
                                {phase.completed}/{phase.sessions.length}
                            </Badge>
                        </TabsTrigger>
                    ))}
                </TabsList>

                {phases.map((phase) => (
                    <TabsContent key={phase.phase} value={phase.phase}>
                        <Card className="bg-white/5 border-white/10 overflow-hidden">
                            <CardHeader className={`bg-gradient-to-r ${getPhaseColor(phase.phase)} p-6`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-white text-2xl">
                                            {phase.name}
                                        </CardTitle>
                                        <CardDescription className="text-white/80 mt-1">
                                            {phase.description}
                                        </CardDescription>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-white/80 text-sm">Progress</p>
                                        <p className="text-white text-2xl font-bold">
                                            {phase.completed}/{phase.sessions.length}
                                        </p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y divide-white/5">
                                    {sessions
                                        .filter(s => s.phase === phase.phase)
                                        .map((session) => (
                                            <div 
                                                key={session.session_id}
                                                className={`p-5 flex items-center gap-5 transition-all ${
                                                    session.status === 'locked' 
                                                        ? 'opacity-50 cursor-not-allowed' 
                                                        : 'cursor-pointer hover:bg-white/5'
                                                }`}
                                                onClick={() => {
                                                    if (session.status !== 'locked') {
                                                        navigate(`/inside-sales/session/${session.session_id}`);
                                                    }
                                                }}
                                                data-testid={`session-${session.session_id}`}
                                            >
                                                {/* Session Number */}
                                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                                                    session.status === 'completed' 
                                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                                        : session.status === 'in_progress'
                                                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                                        : session.status === 'available'
                                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                                        : 'bg-white/5 text-white/30 border border-white/10'
                                                }`}>
                                                    {session.status === 'completed' ? (
                                                        <CheckCircle2 className="w-6 h-6" />
                                                    ) : (
                                                        session.session_id
                                                    )}
                                                </div>

                                                {/* Session Info */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-semibold text-white truncate">
                                                        {session.title}
                                                    </h3>
                                                    <p className="text-sm text-white/50 truncate mt-0.5">
                                                        {session.description}
                                                    </p>
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            ~{session.estimated_duration}min
                                                        </span>
                                                        {session.score && (
                                                            <span className="flex items-center gap-1 text-emerald-400">
                                                                <Trophy className="w-3 h-3" />
                                                                {Math.round(session.score * 100)}%
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Progress/Status */}
                                                <div className="flex items-center gap-4">
                                                    {session.status === 'in_progress' && (
                                                        <div className="w-24">
                                                            <Progress value={session.progress_percent} className="h-2 bg-white/10" />
                                                            <p className="text-xs text-center mt-1 text-white/40">
                                                                {session.progress_percent}%
                                                            </p>
                                                        </div>
                                                    )}
                                                    {getStatusBadge(session.status)}
                                                    {session.status !== 'locked' && (
                                                        <ChevronRight className="w-5 h-5 text-white/30" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                ))}
            </Tabs>

            {/* Voice-Only Notice */}
            <Card className="bg-indigo-500/5 border-indigo-500/20">
                <CardContent className="p-6 flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                        <Volume2 className="w-7 h-7 text-indigo-400" />
                    </div>
                    <div>
                        <h3 className="font-bold text-white text-lg">Voice-Only Interactive Training</h3>
                        <p className="text-white/60 mt-2 leading-relaxed">
                            This training is 100% voice-based. You&apos;ll listen to AI-powered coaching and respond 
                            verbally to questions and simulations. For the best experience:
                        </p>
                        <div className="flex flex-wrap gap-4 mt-4">
                            <div className="flex items-center gap-2 text-sm text-white/50">
                                <Headphones className="w-4 h-4 text-indigo-400" />
                                Use headphones
                            </div>
                            <div className="flex items-center gap-2 text-sm text-white/50">
                                <Mic className="w-4 h-4 text-indigo-400" />
                                Working microphone
                            </div>
                            <div className="flex items-center gap-2 text-sm text-white/50">
                                <Volume2 className="w-4 h-4 text-indigo-400" />
                                Quiet environment
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
            </div>
        </div>
    );
};

export default InsideSalesDashboard;
